export interface NextDrawTimeFormatOptions {
  locale?: string | string[];
  now?: Date;
  timeZone?: string;
}

const MS_PER_DAY = 24 * 60 * 60 * 1000;

export function formatNextDrawHeading(
  draw: Date,
  options: NextDrawTimeFormatOptions = {}
): string {
  if (!isValidDate(draw)) return '';
  const weekday = formatDateTime(draw, options, { weekday: 'short' });
  const time = formatDrawTime(draw, options);
  return `${weekday} ${time}`;
}

export function formatNextDrawSubheading(
  draw: Date,
  options: NextDrawTimeFormatOptions = {}
): string {
  if (!isValidDate(draw)) return '';
  const dayLabel = relativeDayLabel(draw, options);
  const time = formatDrawTime(draw, options);
  return `${dayLabel} at ${time}`;
}

function relativeDayLabel(target: Date, options: NextDrawTimeFormatOptions): string {
  const now = options.now ?? new Date();
  const dayDiff = calendarDayIndex(target, options.timeZone) - calendarDayIndex(now, options.timeZone);
  if (dayDiff === 0) return 'Today';
  if (dayDiff === 1) return 'Tomorrow';
  return formatDateTime(target, options, { weekday: 'long' });
}

function calendarDayIndex(date: Date, timeZone?: string): number {
  const parts = formatDateParts(date, timeZone);
  return Math.floor(Date.UTC(parts.year, parts.month - 1, parts.day) / MS_PER_DAY);
}

function formatDateParts(date: Date, timeZone?: string): { year: number; month: number; day: number } {
  const parts = makeFormatter('en-US', timeZone, {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).formatToParts(date);
  const valueFor = (type: string) => parts.find((part) => part.type === type)?.value ?? '0';
  return {
    year: Number(valueFor('year')),
    month: Number(valueFor('month')),
    day: Number(valueFor('day')),
  };
}

function formatDrawTime(draw: Date, options: NextDrawTimeFormatOptions): string {
  return formatDateTime(draw, options, { hour: 'numeric', minute: '2-digit' });
}

function formatDateTime(
  date: Date,
  options: NextDrawTimeFormatOptions,
  formatOptions: Intl.DateTimeFormatOptions
): string {
  return cleanDisplay(makeFormatter(options.locale, options.timeZone, formatOptions).format(date));
}

function makeFormatter(
  locale: string | string[] | undefined,
  timeZone: string | undefined,
  options: Intl.DateTimeFormatOptions
): Intl.DateTimeFormat {
  const formatterOptions: Intl.DateTimeFormatOptions = { ...options };
  if (timeZone) formatterOptions.timeZone = timeZone;
  return new Intl.DateTimeFormat(locale, formatterOptions);
}

function cleanDisplay(value: string): string {
  return value.replace(/[\u00a0\u202f]/g, ' ');
}

function isValidDate(date: Date): boolean {
  return !Number.isNaN(date.getTime());
}
